import { doc, getDoc, type Unsubscribe } from '@firebase/firestore';
import { db } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { Structure } from '../types';
import { mapDoc } from '@utils/firestore';
import { StructureSchema } from '../schemas/firestoreSchemas';

// `claimSummary` is a client-side derivation from the live claims
// subscription (see `joinClaim` in structuresStore). Historical data left
// `claimSummary` on the `structures/*` docs directly, so strip it at the
// API boundary — otherwise a stale server value would contradict the
// live claim state and the detail sheet would flip-flop depending on
// whether the data last came from the GeoJSON overlay or a Firestore read.
function stripClaimSummary(structure: Structure | null): Structure | null {
  if (!structure || structure.claimSummary == null) return structure;
  const { claimSummary: _discard, ...rest } = structure;
  void _discard;
  return rest as Structure;
}

export const structuresApi = {
  async getStructure(structureId: string): Promise<Structure | null> {
    const docRef = doc(db, COLLECTIONS.STRUCTURES, structureId);
    const snapshot = await getDoc(docRef);
    return stripClaimSummary(mapDoc(snapshot, StructureSchema));
  },

  // Subscribe to a single structure. The consumer gets live updates whenever
  // the document changes — including denormalized fields like
  // `progressSnapshot` that the checklist path writes. Returns the
  // unsubscribe function; callers MUST invoke it on unmount.
  subscribeToStructure(
    structureId: string,
    onChange: (structure: Structure | null) => void,
    onError?: (err: Error) => void
  ): Unsubscribe {
    const docRef = doc(db, COLLECTIONS.STRUCTURES, structureId);
    return onSnapshotWithRetry(
      docRef,
      (snapshot) => onChange(stripClaimSummary(mapDoc(snapshot, StructureSchema))),
      (err) => onError?.(err)
    );
  },
};
